
import { StateManagerInterface, DataManagerInterface, ConfigManagerInterface, AppConfig, AudioEngineInterface, EQExporterInterface, EQSetting, TestResult, NotificationSystemInterface } from '../../types';
import { buildParametricFilters, calculateParametricPreamp } from '../../core/parametric-eq';
import { Logger } from '../../core/logger';
const logger = Logger.createComponentLogger('EventHandlers');

class EventHandlers {
    stateManager: StateManagerInterface;
    dataManager: DataManagerInterface;
    configManager: ConfigManagerInterface;
    appConfig: AppConfig;
    audioEngine: AudioEngineInterface;
    graphRenderer: { render: () => void };
    eqExporter: EQExporterInterface;
    notificationSystem: NotificationSystemInterface;
    modalDialog: { showConfirmation: (message: string, onConfirm?: () => void, onCancel?: () => void) => void };
    appController: { generateEqSettings: () => EQSetting[] | undefined; drawGraph: () => boolean };
    
    // UI Element Properties
    presetFrequencies: number[];
    currentPresetIndex: number;
    startStopBtn: HTMLButtonElement | null;
    earSelect: HTMLSelectElement | null;
    noiseVolumeSlider: HTMLInputElement | null;
    noiseVolumeLabel: HTMLElement | null;
    sineVolumeSlider: HTMLInputElement | null;
    sineVolumeLabel: HTMLElement | null;
    frequencySlider: HTMLInputElement | null;
    frequencyLabel: HTMLElement | null;
    saveBtn: HTMLButtonElement | null;
    loadBtn: HTMLButtonElement | null;
    compareBtn: HTMLButtonElement | null;
    resetBtn: HTMLButtonElement | null;
    calculateEqBtn: HTMLButtonElement | null;
    prevFreqBtn: HTMLButtonElement | null;
    nextFreqBtn: HTMLButtonElement | null;
    frequencyIndicatorContainer: HTMLElement | null;
    exportBtn: HTMLButtonElement | null;
    exportFormatSelect: HTMLSelectElement | null;
    qualitySelect: HTMLInputElement | null;
    qualityLabel: HTMLElement | null;
    strengthSelect: HTMLSelectElement | null;
    loadBtnHidden: HTMLElement | null;
    contourSelect: HTMLSelectElement | null;
    sinePlaybackModeSelect: HTMLSelectElement | null;
    volumeDbStepSelect: HTMLSelectElement | null;
    eqResultsDisplayed = false;

    constructor(stateManager: StateManagerInterface, dataManager: DataManagerInterface, configManager: ConfigManagerInterface, appConfig: AppConfig, audioEngine: AudioEngineInterface, graphRenderer: { render: () => void }, eqExporter: EQExporterInterface, notificationSystem: NotificationSystemInterface, modalDialog: { showConfirmation: (message: string, onConfirm?: () => void, onCancel?: () => void) => void }, appController: { generateEqSettings: () => EQSetting[] | undefined; drawGraph: () => boolean }) {
        this.stateManager = stateManager;
        this.dataManager = dataManager;
        this.configManager = configManager;
        this.appConfig = appConfig;
        this.audioEngine = audioEngine;
        this.graphRenderer = graphRenderer;
        this.eqExporter = eqExporter;
        this.notificationSystem = notificationSystem;
        this.modalDialog = modalDialog;
        this.appController = appController;
        
        this.presetFrequencies = stateManager.getPresetFrequencies();
        this.currentPresetIndex = stateManager.getCurrentPresetIndex();
        
        logger.info('EventHandlers initialized');
    }

    /**
     * Initialize all event handlers when DOM is ready
     */
    initializeEventHandlers() {
        logger.info('DOM loaded, initializing application...');
        
        // Try to draw graph immediately with empty data
        try {
            this.drawGraph();
            logger.debug('Initial graph drawn successfully');
        } catch (error) {
            logger.error('Error drawing initial graph:', error);
        }
        
        // Load ISO data and update StateManager
        this.loadISOData();
        
        // Initialize UI element references
        this.initializeUIElements();
        
        // Set up all event listeners
        this.setupEventListeners();
        
        // Initialize frequency indicators
        this.createFrequencyIndicators();
    }

    /**
     * Load ISO 226 contour data
     */
    loadISOData() {
        fetch('iso226.json')
            .then(response => {
                logger.debug('ISO226 fetch response:', response.status);
                return response.json();
            })
            .then(data => {
                logger.info('ISO226 data loaded successfully');
                this.stateManager.setEqualLoudnessContours(data);
                this.drawGraph(); // Redraw with contour data
            })
            .catch(error => {
                logger.error('Error loading ISO226 data:', error);
                // Even if ISO data fails, we should still have a basic graph
                this.stateManager.setEqualLoudnessContours({});
                this.drawGraph();
            });
    }

    /**
     * Initialize UI element references
     */
    initializeUIElements() {
        this.startStopBtn = document.getElementById('start-stop-btn') as HTMLButtonElement | null;
        this.earSelect = document.getElementById('ear-select') as HTMLSelectElement | null;
        this.noiseVolumeSlider = document.getElementById('noise-volume') as HTMLInputElement | null;
        this.noiseVolumeLabel = document.getElementById('noise-volume-label') as HTMLElement | null;
        this.sineVolumeSlider = document.getElementById('sine-volume') as HTMLInputElement | null;
        this.sineVolumeLabel = document.getElementById('sine-volume-label') as HTMLElement | null;
        this.frequencySlider = document.getElementById('frequency-slider') as HTMLInputElement | null;
        this.frequencyLabel = document.getElementById('frequency-label') as HTMLElement | null;
        this.saveBtn = document.getElementById('save-btn') as HTMLButtonElement | null;
        this.loadBtn = document.getElementById('load-btn') as HTMLButtonElement | null;
        this.compareBtn = document.getElementById('compare-btn') as HTMLButtonElement | null;
        this.resetBtn = document.getElementById('reset-btn') as HTMLButtonElement | null;
        this.calculateEqBtn = document.getElementById('calculate-eq-btn') as HTMLButtonElement | null;
        this.prevFreqBtn = document.getElementById('prev-freq-btn') as HTMLButtonElement | null;
        this.nextFreqBtn = document.getElementById('next-freq-btn') as HTMLButtonElement | null;
        this.frequencyIndicatorContainer = document.getElementById('frequency-indicator-container') as HTMLElement | null;
        this.exportBtn = document.getElementById('export-btn') as HTMLButtonElement | null;
        this.exportFormatSelect = document.getElementById('export-format-select') as HTMLSelectElement | null;
        this.exportFormatSelect = document.getElementById('export-format') as HTMLSelectElement | null;
        this.qualitySelect = document.getElementById('parametric-quality') as HTMLInputElement | null;
        this.qualityLabel = document.getElementById('quality-label') as HTMLElement | null;
        this.strengthSelect = document.getElementById('eq-strength') as HTMLSelectElement | null;

        // Initialize strength/quality selectors from persisted config
        const cfg = this.configManager.getConfig();
        if (this.strengthSelect && Number.isFinite(cfg.strength)) this.strengthSelect.value = String(cfg.strength);
        if (this.qualitySelect && Number.isFinite(cfg.peakQuality)) this.qualitySelect.value = String(cfg.peakQuality);
        this.loadBtnHidden = document.getElementById('load-btn-hidden') as HTMLElement | null;
        this.contourSelect = document.getElementById('contour-select') as HTMLSelectElement | null;
        this.sinePlaybackModeSelect = document.getElementById('sine-playback-mode') as HTMLSelectElement | null;
        if (this.sinePlaybackModeSelect && typeof this.audioEngine.setPlaybackMode === 'function') {
            // Apply initial mode (audioEngine will no-op if audioContext not created yet)
            this.audioEngine.setPlaybackMode(this.sinePlaybackModeSelect.value as 'continuous' | 'intermittent');
        }

        // Volume dB step select - initialize and apply to volume sliders
        this.volumeDbStepSelect = document.getElementById('volume-db-step') as HTMLSelectElement | null;
        const initialStep = this.volumeDbStepSelect?.value ?? '1';
        if (this.noiseVolumeSlider) this.noiseVolumeSlider.step = initialStep;
        if (this.sineVolumeSlider) this.sineVolumeSlider.step = initialStep;
    }

    /**
     * Set up all event listeners
     */
    setupEventListeners() {
        // Audio control events
        this.startStopBtn?.addEventListener('click', () => this.handleStartStop());
        this.noiseVolumeSlider?.addEventListener('input', () => this.handleNoiseVolumeChange());
        this.sineVolumeSlider?.addEventListener('input', () => this.handleSineVolumeChange());
        this.frequencySlider?.addEventListener('input', () => this.handleFrequencyChange());
        
        // Navigation events
        this.prevFreqBtn?.addEventListener('click', () => this.handlePrevFrequency());
        this.nextFreqBtn?.addEventListener('click', () => this.handleNextFrequency());
        
        // UI update events
        this.earSelect?.addEventListener('change', () => this.handleEarChange());
        this.contourSelect?.addEventListener('change', () => this.drawGraph());
        // Strength/quality selectors -> persist to config (read fresh at EQ generation),
        // then refresh displayed results so they match what Export would write now
        this.strengthSelect?.addEventListener('change', () => {
            const strength = Number(this.strengthSelect?.value);
            if (Number.isFinite(strength)) this.configManager.updateConfig({ strength });
            this.refreshEqualizationResults();
        });
        this.qualitySelect?.addEventListener('change', () => {
            const peakQuality = Number(this.qualitySelect?.value);
            if (Number.isFinite(peakQuality)) this.configManager.updateConfig({ peakQuality });
            this.refreshEqualizationResults();
        });
        // Sine playback mode select -> update audio engine
        this.sinePlaybackModeSelect?.addEventListener('change', () => {
            const mode = (this.sinePlaybackModeSelect?.value ?? 'continuous') as 'continuous' | 'intermittent';
            if (typeof this.audioEngine.setPlaybackMode === 'function') {
                this.audioEngine.setPlaybackMode(mode);
            }
        });

        // Volume dB step select -> update slider step attributes
        this.volumeDbStepSelect?.addEventListener('change', () => {
            this.handleVolumeDbStepChange();
        });
        
        // File operations
        this.saveBtn?.addEventListener('click', () => this.saveConfiguration());
        this.loadBtnHidden?.addEventListener('change', (event) => this.loadConfiguration(event));
        this.resetBtn?.addEventListener('click', () => this.resetResults());
        this.compareBtn?.addEventListener('click', () => this.handleCompare());
        this.calculateEqBtn?.addEventListener('click', () => this.calculateEqualization());
        
        // Export events
        this.exportBtn?.addEventListener('click', () => { void this.exportConfiguration(); });
        this.exportFormatSelect?.addEventListener('change', () => this.handleExportFormatChange());
        
        // Configuration slider setup
        this.setupConfigurationSliders();
    }

    /**
     * Handle start/stop button click
     */
    handleStartStop() {
        if (!this.audioEngine.getIsStarted()) {
            this.audioEngine.startAudio();
            this.startStopBtn.textContent = 'Stop';
            this.sineVolumeSlider.disabled = false;
            this.frequencySlider.disabled = false;
            this.prevFreqBtn.disabled = false;
            this.nextFreqBtn.disabled = false;
            // Set initial volume based on slider, but only when starting
            const noiseValStr = this.noiseVolumeSlider?.value ?? '0';
            const noiseVal = parseFloat(noiseValStr);
            const sineValStr = this.sineVolumeSlider?.value ?? '0';
            const sineVal = parseFloat(sineValStr);
            this.audioEngine.setNoiseVolume(noiseVal);
            this.audioEngine.setSineVolume(sineVal);
            
            // Update labels to match current slider values (in case browser restored different values)
            if (this.noiseVolumeLabel) this.noiseVolumeLabel.textContent = `${noiseVal} dB`;
            if (this.sineVolumeLabel) this.sineVolumeLabel.textContent = `${sineVal} dB`;
        } else {
            this.audioEngine.stopAudio();
            this.startStopBtn.textContent = 'Start';
            this.sineVolumeSlider.disabled = true;
            this.frequencySlider.disabled = true;
            this.prevFreqBtn.disabled = true;
            this.nextFreqBtn.disabled = true;
        }
    }

    /**
     * Handle noise volume slider change
     */
    handleNoiseVolumeChange() {
        const dbValueStr = this.noiseVolumeSlider?.value ?? '0';
        if (this.noiseVolumeLabel) this.noiseVolumeLabel.textContent = `${dbValueStr} dB`;
        if (this.audioEngine.getIsStarted()) {
            this.audioEngine.setNoiseVolume(parseFloat(dbValueStr));
        }
    }

    /**
    * Handle sine volume slider change
    */
    handleSineVolumeChange() {
        const dbValueStr = this.sineVolumeSlider?.value ?? '0';
        if (this.sineVolumeLabel) this.sineVolumeLabel.textContent = `${dbValueStr} dB`;
        if (this.audioEngine.getIsStarted()) {
            const dbNumeric = parseFloat(dbValueStr);
            this.audioEngine.setSineVolume(dbNumeric);
            const ear = (this.earSelect?.value ?? 'both') as 'left' | 'right' | 'both';
            this.stateManager.addResult(this.stateManager.getCurrentFrequency(), dbNumeric, ear);
        }
    }

    /**
     * Handle volume dB step select change
     */
    handleVolumeDbStepChange() {
        const stepStr = this.volumeDbStepSelect?.value ?? '1';
        if (this.noiseVolumeSlider) this.noiseVolumeSlider.step = stepStr;
        if (this.sineVolumeSlider) this.sineVolumeSlider.step = stepStr;
    }

    /**
     * Handle frequency slider change
     */
    handleFrequencyChange() {
        this.updateFrequencyDisplay();
        if (this.audioEngine.getIsStarted()) {
            this.audioEngine.setSineFrequency(this.stateManager.getCurrentFrequency());

            // Update sine volume slider to reflect the stored value for the new frequency
            const ear = (this.earSelect?.value ?? 'both') as 'left' | 'right' | 'both';
            const testResults = this.stateManager.getAllResults();
            const results = testResults[ear];
            const currentFrequency = this.stateManager.getCurrentFrequency();
            const existingPoint = results.find((p: TestResult) => p.frequency === currentFrequency);

            let dbValue;
            if (existingPoint) {
                dbValue = existingPoint.db;
            } else {
                // When moving the slider to a frequency without a stored result,
                // use a default value. TODO: Implement proper interpolation
                dbValue = -50; // Default volume
            }

            const numericDb = typeof dbValue === 'string' ? parseFloat(dbValue) : dbValue;
            if (this.sineVolumeSlider) this.sineVolumeSlider.value = String(numericDb);
            if (this.sineVolumeLabel) this.sineVolumeLabel.textContent = `${Number.isInteger(numericDb) ? numericDb : numericDb.toFixed(1)} dB`;
            this.audioEngine.setSineVolume(numericDb); // Update the actual audio gain
        }
    }

    /**
     * Handle previous frequency button click
     */
    handlePrevFrequency() {
        if (this.currentPresetIndex > 0) {
            this.currentPresetIndex--;
            if (this.frequencySlider) {
                this.frequencySlider.value = String(this.presetFrequencies[this.currentPresetIndex]);
                this.frequencySlider.dispatchEvent(new Event('input'));
            }
        }
    }

    /**
     * Handle next frequency button click
     */
    handleNextFrequency() {
        if (this.currentPresetIndex < this.presetFrequencies.length - 1) {
            this.currentPresetIndex++;
            if (this.frequencySlider) {
                this.frequencySlider.value = String(this.presetFrequencies[this.currentPresetIndex]);
                this.frequencySlider.dispatchEvent(new Event('input'));
            }
        }
    }

    /**
     * Handle ear selection change
     */
    handleEarChange() {
        logger.info(`Ear selection changed to: ${this.earSelect.value}`);
        this.drawGraph(); // Redraw graph for the selected ear
        this.updateAllIndicators();
    }

    /**
     * Handle compare button click (placeholder)
     */
    handleCompare() {
        // For now, this is a placeholder. A full implementation
        // would likely open a file dialog or a second localStorage slot.
        logger.info('Compare functionality not fully implemented yet.');
        
        // Use notification system
        this.notificationSystem.showInfo('Compare functionality is coming soon!');
    }

    /**
     * Handle export format change
     */
    handleExportFormatChange() {
        if ((this.exportFormatSelect?.value ?? '') === 'graphic') {
            if (this.qualitySelect) this.qualitySelect.style.display = 'none';
            if (this.qualityLabel) this.qualityLabel.style.display = 'none';
        } else {
            if (this.qualitySelect) this.qualitySelect.style.display = 'inline-block';
            if (this.qualityLabel) this.qualityLabel.style.display = 'inline-block';
        }
    }

    /**
     * Update frequency display and state
     */
    updateFrequencyDisplay() {
        const freqValue = this.frequencySlider?.value ?? '0';
        if (this.frequencyLabel) this.frequencyLabel.textContent = `${freqValue} Hz`;
        this.stateManager.setCurrentFrequency(parseFloat(freqValue));

        // Update dot indicators
        const dots = document.querySelectorAll('.freq-indicator-dot');
        const currentFreq = this.stateManager.getCurrentFrequency();
        const closestPresetIndex = this.presetFrequencies.reduce((prev, curr, index) => {
            return (Math.abs(curr - currentFreq) < Math.abs(this.presetFrequencies[prev] - currentFreq) ? index : prev);
        }, 0);

        dots.forEach((dot, index) => {
            dot.classList.remove('current');
            if (index === closestPresetIndex) {
                dot.classList.add('current');
            }
        });
    }

    /**
     * Update all frequency indicator dots
     */
    updateAllIndicators() {
        const dots = document.querySelectorAll('.freq-indicator-dot');
        dots.forEach(dot => dot.classList.remove('adjusted')); // Clear all

        const earEl = document.getElementById('ear-select') as HTMLSelectElement | null;
        const ear = earEl?.value ?? 'both';
        const results = this.stateManager.getResults(ear);
        results.forEach((point: TestResult) => {
            const presetIndex = this.presetFrequencies.indexOf(point.frequency);
            if (presetIndex !== -1) {
                const dot = document.querySelector(`.freq-indicator-dot[data-index='${presetIndex}']`);
                if (dot) {
                    dot.classList.add('adjusted');
                }
            }
        });
    }

    /**
     * Create frequency indicator dots
     */
    createFrequencyIndicators() {
        if (this.frequencyIndicatorContainer) {
            this.frequencyIndicatorContainer.innerHTML = '';
            this.presetFrequencies.forEach((freq, index) => {
                const dot = document.createElement('div');
                dot.className = 'freq-indicator-dot';
                dot.dataset.index = index.toString();
                dot.addEventListener('click', () => {
                    this.currentPresetIndex = index;
                    if (this.frequencySlider) {
                        this.frequencySlider.value = String(this.presetFrequencies[this.currentPresetIndex]);
                        this.frequencySlider.dispatchEvent(new Event('input'));
                    }
                });
                this.frequencyIndicatorContainer.appendChild(dot);
            });
        }
    }

    /**
     * Save configuration
     */
    saveConfiguration() {
        const noiseEl = document.getElementById('noise-volume') as HTMLInputElement | null;
        const contourEl = document.getElementById('contour-select') as HTMLSelectElement | null;
        const uiState = {
            noiseVolume: noiseEl ? parseFloat(noiseEl.value) : 0,
            contour: contourEl?.value,
            sinePlaybackMode: this.sinePlaybackModeSelect?.value ?? 'continuous',
            volumeDbStep: this.volumeDbStepSelect?.value ?? '1'
        };
        this.dataManager.saveConfiguration(this.stateManager.getAllResults(), uiState, this.appConfig);
    }

    /**
     * Load configuration from file
     */
    loadConfiguration(event: Event): void {
        this.dataManager.loadConfiguration(event, (data: any) => {
            // Update test results through StateManager
            const newTestResults = data.testResults || { both: [], left: [], right: [] };
            this.stateManager.testResults = newTestResults;

            // Update UI state
            if (data.uiState) {
                if (data.uiState.noiseVolume !== undefined) {
                    const noiseEl = document.getElementById('noise-volume') as HTMLInputElement | null;
                    const noiseLabelEl = document.getElementById('noise-volume-label') as HTMLElement | null;
                    if (noiseEl) noiseEl.value = data.uiState.noiseVolume;
                    if (noiseLabelEl) noiseLabelEl.textContent = `${data.uiState.noiseVolume} dB`;
                }
                if (data.uiState.contour !== undefined) {
                    const contourEl = document.getElementById('contour-select') as HTMLSelectElement | null;
                    if (contourEl) contourEl.value = data.uiState.contour;
                }
                if (data.uiState.sinePlaybackMode !== undefined) {
                    const sineModeEl = document.getElementById('sine-playback-mode') as HTMLSelectElement | null;
                    if (sineModeEl) sineModeEl.value = data.uiState.sinePlaybackMode;
                    if (typeof this.audioEngine.setPlaybackMode === 'function') {
                        this.audioEngine.setPlaybackMode(data.uiState.sinePlaybackMode);
                    }
                }
                if (data.uiState.volumeDbStep !== undefined) {
                    const dbStepEl = document.getElementById('volume-db-step') as HTMLSelectElement | null;
                    if (dbStepEl) dbStepEl.value = String(data.uiState.volumeDbStep);
                    if (this.noiseVolumeSlider) this.noiseVolumeSlider.step = String(data.uiState.volumeDbStep);
                    if (this.sineVolumeSlider) this.sineVolumeSlider.step = String(data.uiState.volumeDbStep);
                }
            }

            this.updateAllIndicators();
            this.drawGraph();
        });
    }

    /**
     * Reset all results
     */
    resetResults() {
        // Use modal dialog
        this.modalDialog.showConfirmation(
            'Are you sure you want to reset all test results?',
            () => {
                // Confirmed - reset results
                this.stateManager.clearResults('all');
                this.updateAllIndicators();
                this.drawGraph();
            },
            () => {
                // Cancelled - do nothing
            }
        );
    }

    /**
     * Calculate equalization
     */
    calculateEqualization() {
        // Generate EQ settings from current test results
        const eqSettings = this.appController.generateEqSettings();
        
        if (!eqSettings || eqSettings.length === 0) {
            this.notificationSystem.showWarning('No test results available to calculate equalization. Please complete some hearing tests first.');
            return;
        }

        // Display the EQ results in the eq-results container
        this.displayEqualizationResults(eqSettings);
    }

    /**
     * Re-render the results with current settings, only if Calculate has already shown them
     */
    refreshEqualizationResults(): void {
        if (!this.eqResultsDisplayed) return;
        const eqSettings = this.appController.generateEqSettings();
        if (eqSettings && eqSettings.length > 0) this.displayEqualizationResults(eqSettings);
    }

    /**
     * Display equalization results in the UI
     * @param {Array} eqSettings - Array of EQ settings
     */
    displayEqualizationResults(eqSettings: EQSetting[]): void {
        const resultsContainer = document.getElementById('eq-results');
        if (!resultsContainer) {
            logger.error('EQ results container not found');
            return;
        }

        logger.debug('=== DISPLAY RESULTS DEBUG ===');
        logger.debug('EQ settings received:', eqSettings);
        logger.debug('EqExporter available:', !!this.eqExporter);

        // Create table HTML
        let html = '<table class="eq-table">';
        html += '<thead><tr><th>Frequency (Hz)</th><th>Gain (dB)</th><th>Q Factor</th><th>Status</th></tr></thead>';
        html += '<tbody>';

        // Rows, gain range and preamp all come from the exact filters the parametric export
        // writes, so the table always matches the downloaded file.
        const filters = buildParametricFilters(eqSettings, this.parametricExportOptions());
        const isTested = (freq: number) => eqSettings.some((setting: EQSetting) => Math.abs(setting.frequency - freq) < 1);

        filters.forEach(({ frequency, gain, q }) => {
            const tested = isTested(frequency);
            html += `<tr class="${tested ? 'tested-freq' : 'interpolated-freq'}">`;
            html += `<td>${frequency}</td>`;
            html += `<td>${gain.toFixed(2)}</td>`;
            html += `<td>${q.toFixed(2)}</td>`;
            html += `<td>${tested ? 'Tested' : 'Interpolated'}</td>`;
            html += `</tr>`;
        });

        html += '</tbody></table>';

        // Add summary information
        const testedCount = eqSettings.length;
        const interpolatedCount = this.presetFrequencies.length - testedCount;
        const gains = filters.map(f => f.gain);
        const minGain = Math.min(...gains);
        const maxGain = Math.max(...gains);

        const preamp = calculateParametricPreamp(filters);
        const preampText = preamp >= 0 ? `+${preamp.toFixed(1)}` : preamp.toFixed(1);

        html += `<div class="eq-summary">`;
        html += `<p><strong>EQ Summary:</strong></p>`;
        html += `<p>Total filters: ${this.presetFrequencies.length}</p>`;
        html += `<p>Tested frequencies: ${testedCount}</p>`;
        html += `<p>Interpolated frequencies: ${interpolatedCount}</p>`;
        html += `<p>Gain range: ${minGain.toFixed(2)} dB to ${maxGain.toFixed(2)} dB</p>`;
        html += `<p class="preamp-recommendation"><strong>Recommended Preamp: ${preampText} dB</strong></p>`;
        html += `</div>`;

        resultsContainer.innerHTML = html;
        this.eqResultsDisplayed = true;
        
        logger.info('EQ results displayed:', this.presetFrequencies.length, 'total filters (', testedCount, 'tested,', interpolatedCount, 'interpolated)');
        logger.info('Gain range:', minGain.toFixed(2), 'to', maxGain.toFixed(2), 'dB');
    }

    /**
     * Options shared by the parametric export and the "Recommended Preamp" summary
     */
    parametricExportOptions(): { peakQuality: number; presetFrequencies: number[] } {
        const peakQualityRaw = Number(this.qualitySelect?.value);
        return {
            peakQuality: Number.isFinite(peakQualityRaw) ? peakQualityRaw : 0,
            presetFrequencies: this.presetFrequencies
        };
    }

    /**
     * Export configuration in selected format
     */
    async exportConfiguration(): Promise<void> {
        const format = String(this.exportFormatSelect?.value || '').trim();
        
        // Get EQ settings
        const eqSettings = this.appController.generateEqSettings();
        
        if (!eqSettings) {
            this.notificationSystem.showWarning('No test results to export or no contour selected.');
            return;
        }

        // PARAMETRIC export remains synchronous and unchanged
        if (format === 'parametric') {
            const content = this.eqExporter.exportParametric(eqSettings, this.parametricExportOptions());
            const filename = 'parametric_eq.txt';
            if (!content.trim()) {
                this.notificationSystem.showError('No EQ data to export.');
                return;
            }
            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            return;
        }

        // GRAPHIC export: AutoEQ-style "GraphicEQ: f g; ..." text (importable by Wavelet / EqualizerAPO)
        if (format === 'graphic') {
            const content = this.eqExporter.exportGraphic(eqSettings, {
                presetFrequencies: this.presetFrequencies
            });
            const filename = 'graphic_eq.txt';
            if (!content.trim()) {
                this.notificationSystem.showError('No EQ data to export.');
                return;
            }
            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            return;
        }

        // Unknown format
        this.notificationSystem.showError('Please select a valid export format.');
        return;
    }

    /**
     * Set up configuration sliders
     */
    setupConfigurationSliders() {
        const frequencyRangeSlider = document.getElementById('frequency-range') as HTMLInputElement | null;
        if (frequencyRangeSlider) {
            frequencyRangeSlider.addEventListener('input', (e) => {
                const v = parseFloat((e.target as HTMLInputElement).value);
                const currentMin = this.configManager.getConfig().minFreq;
                if (v < currentMin) {
                    this.configManager.setFrequencyRange(v, this.configManager.getConfig().maxFreq);
                } else {
                    this.configManager.setFrequencyRange(currentMin, v);
                }
                // Update local appConfig reference
                Object.assign(this.appConfig, this.configManager.getConfig());
            });
        }
    }

    /**
     * Draw graph (wrapper for compatibility)
     */
    drawGraph() {
        return this.appController.drawGraph();
    }
}

// ES module export
export { EventHandlers };
export default EventHandlers;